// import axios from "axios";
import { CONFIG } from "../../config";
import {
  IPartner,
  IPartnerTeamMember,
  PartnerType,
} from "../../interfaces/partner.interface";
import {
  IPartnerReportRequest,
  IReportRequest,
} from "../../interfaces/requestReport.interface";
import { getTokenizedAxios } from "../../utils/axiosWithTokenGen";

export interface IGetPartnersResponse {
  data: IPartner[];
  totalCount: number;
  currentCount: number;
  pageSize: number;
  currentPage: number;
}

type GetPartnersParams = Partial<{
  partnerId: string;
  name: string;
  partnerType: PartnerType | string;
  currentPage: number;
  pageSize: number;
  country: string;
}>;

export async function getPartnersFromServer(
  params?: GetPartnersParams
): Promise<IGetPartnersResponse> {
  const axios = await getTokenizedAxios();
  const path = "/partners/";

  return axios
    .get(CONFIG.ADMIN_SERVER.BASE_URL + path, { params })
    .then((data) => data?.data);
}
export async function getPartnerFromServer(
  partnerId: string
): Promise<{ partner: IPartner }> {
  const axios = await getTokenizedAxios();
  const path = "/partners/";

  return axios
    .get(CONFIG.ADMIN_SERVER.BASE_URL + path + partnerId)
    .then((data) => data?.data);
}

export function partnerFromDetailsPayload(
  data: { partner?: IPartner } | IPartner | undefined
): IPartner | undefined {
  if (!data || typeof data !== "object") return undefined;
  const nested = "partner" in data ? data.partner : undefined;
  if (nested && typeof nested === "object" && nested._id) return nested;
  if ("_id" in data && data._id) return data as IPartner;
  return undefined;
}

/** Details first when the payload is a partner. A mongoose document spread
 *  (no `_id`) is rejected, so callers fall through to the list until the
 *  details read returns a plain object. */
export async function getPartnerWithPricing(
  partnerId: string
): Promise<IPartner | undefined> {
  try {
    const details = await getPartnerFromServer(partnerId);
    const partner = partnerFromDetailsPayload(details);
    if (partner) return partner;
  } catch {
    // Fall through to the list, which still carries profiles for older callers.
  }
  try {
    const list = await getPartnersFromServer({
      partnerId,
      currentPage: 1,
      pageSize: 1,
    });
    return list?.data?.[0];
  } catch {
    return undefined;
  }
}

export async function getPartnerMarkupReportFromServer(
  reportRequest: IPartnerReportRequest
): Promise<{
  report: string;
  isEmailed: boolean;
  isEmpty: boolean;
}> {
  const axios = await getTokenizedAxios();
  const path = "/partners/reports/markup/";

  return axios
    .get(CONFIG.ADMIN_SERVER.BASE_URL + path, { params: reportRequest })
    .then((data) => data?.data);
}

export async function createPartnerServer(
  partner: Partial<IPartner>
): Promise<IPartner> {
  const axios = await getTokenizedAxios();
  const path = "/partners/create";

  return axios
    .post(CONFIG.ADMIN_SERVER.BASE_URL + path, { partner })
    .then((data) => data?.data);
}

export async function createPartnerTeamMemberServer(params: {
  teamMember: Partial<IPartnerTeamMember>;
  partnerId: string;
}): Promise<IPartner> {
  const axios = await getTokenizedAxios();
  const path = "/partners/teamMembers/create";

  return axios
    .post(CONFIG.ADMIN_SERVER.BASE_URL + path, params)
    .then((data) => data?.data);
}
export async function editPartnerServer({
  partnerId,
  update,
}: {
  partnerId: string;
  update: Partial<IPartner> | Record<string, unknown>;
}): Promise<IPartner> {
  const axios = await getTokenizedAxios();
  const path = "/partners/edit/";

  return axios
    .put(CONFIG.ADMIN_SERVER.BASE_URL + path + partnerId, { update })
    .then((data) => data?.data);
}
